import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-hot-toast';
import { ERC20_ABI, DUSD_ADDRESS } from '../config/contracts';
import { supabase } from '../lib/supabase';

// 黑洞地址 & 预告费
const BLACKHOLE = '0x000000000000000000000000000000000000dEaD';
const ANNOUNCE_FEE = '1000'; // 1000 DUSD

interface Announcement {
  id: number;
  token_address: string;
  token_name: string;
  token_symbol: string;
  total_supply: string;
  estimated_circulation: string;
  planned_launch_time: string;
  description: string;
  website: string;
  creator: string;
  tx_hash: string;
  created_at: string;
}

interface Props {
  account: string | null;
  signer: ethers.Signer | null;
}

// ---- Supabase helpers ----
async function fetchAnnouncements(): Promise<Announcement[]> {
  const { data, error } = await supabase
    .from('announcements')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error(error); return []; }
  return data as Announcement[];
}

async function insertAnnouncement(row: Omit<Announcement, 'id' | 'created_at'>): Promise<boolean> {
  const { error } = await supabase.from('announcements').insert(row);
  if (error) { console.error(error); return false; }
  return true;
}

// ---- Scroll Picker ----
const ITEM_H = 40;
const VISIBLE = 3; // 可见行数（奇数）

interface ScrollColumnProps {
  items: string[];
  value: string;
  onChange: (v: string) => void;
}

const ScrollColumn: React.FC<ScrollColumnProps> = ({ items, value, onChange }) => {
  const ref = useRef<HTMLDivElement>(null);
  const idx = items.indexOf(value);
  const isDragging = useRef(false);
  const startY = useRef(0);
  const startScroll = useRef(0);

  // 滚动到选中项
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = Math.max(0, idx) * ITEM_H;
    }
  }, [idx]);

  const pickFromScroll = useCallback(() => {
    if (!ref.current) return;
    const i = Math.round(ref.current.scrollTop / ITEM_H);
    const clamped = Math.max(0, Math.min(items.length - 1, i));
    if (items[clamped] !== value) onChange(items[clamped]);
  }, [items, value, onChange]);

  const snapScroll = useCallback(() => {
    if (!ref.current) return;
    const i = Math.round(ref.current.scrollTop / ITEM_H);
    ref.current.scrollTop = Math.max(0, Math.min(items.length - 1, i)) * ITEM_H;
    pickFromScroll();
  }, [items, pickFromScroll]);

  return (
    <div style={sc.colWrap}>
      {/* 选中高亮框 */}
      <div style={sc.highlight} />
      <div
        ref={ref}
        style={sc.col}
        onScroll={pickFromScroll}
        onScrollCapture={pickFromScroll}
        onTouchStart={e => {
          isDragging.current = true;
          startY.current = e.touches[0].clientY;
          startScroll.current = ref.current?.scrollTop ?? 0;
        }}
        onTouchMove={e => {
          if (!ref.current) return;
          const dy = startY.current - e.touches[0].clientY;
          ref.current.scrollTop = startScroll.current + dy;
        }}
        onTouchEnd={snapScroll}
        onMouseDown={e => {
          isDragging.current = true;
          startY.current = e.clientY;
          startScroll.current = ref.current?.scrollTop ?? 0;
        }}
        onMouseMove={e => {
          if (!isDragging.current || !ref.current) return;
          const dy = startY.current - e.clientY;
          ref.current.scrollTop = startScroll.current + dy;
        }}
        onMouseUp={snapScroll}
        onMouseLeave={() => { if (isDragging.current) { isDragging.current = false; snapScroll(); } }}
      >
        {/* 上下各补 2 项空白，让首尾也能居中 */}
        <div style={{ height: ITEM_H * Math.floor(VISIBLE / 2) }} />
        {items.map(item => (
          <div
            key={item}
            style={{ ...sc.item, ...(item === value ? sc.itemActive : {}) }}
            onClick={() => {
              onChange(item);
              if (ref.current) ref.current.scrollTop = items.indexOf(item) * ITEM_H;
            }}
          >
            {item}
          </div>
        ))}
        <div style={{ height: ITEM_H * Math.floor(VISIBLE / 2) }} />
      </div>
    </div>
  );
};

const sc: Record<string, React.CSSProperties> = {
  colWrap: {
    position: 'relative',
    flex: 1,
    height: ITEM_H * VISIBLE,
    overflow: 'hidden',
  },
  highlight: {
    position: 'absolute',
    left: 0, right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: ITEM_H,
    background: 'rgba(0,212,255,0.08)',
    border: '1px solid rgba(0,212,255,0.25)',
    borderRadius: 8,
    pointerEvents: 'none',
    zIndex: 1,
  },
  col: {
    height: '100%',
    overflowY: 'scroll',
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
    WebkitOverflowScrolling: 'touch' as any,
    userSelect: 'none' as const,
    cursor: 'grab',
  },
  item: {
    height: ITEM_H,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    color: '#3d5070',
    transition: 'color 0.15s',
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  itemActive: {
    color: '#e8eef7',
    fontWeight: 700,
    fontSize: 18,
  },
};

// 生成年月日时分的选项列表
const now = new Date();
const YEARS  = Array.from({ length: 5 },  (_, i) => String(now.getFullYear() + i));
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const HOURS  = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINS   = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function getDays(year: string, month: string): string[] {
  const count = new Date(Number(year), Number(month), 0).getDate();
  return Array.from({ length: count }, (_, i) => String(i + 1).padStart(2, '0'));
}

interface DateTimePickerProps {
  onChange: (iso: string) => void; // 返回 "YYYY-MM-DDTHH:mm"
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({ onChange }) => {
  const [year,  setYear]  = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [day,   setDay]   = useState(String(now.getDate()).padStart(2, '0'));
  const [hour,  setHour]  = useState(String(now.getHours()).padStart(2, '0'));
  const [min,   setMin]   = useState('00');

  const days = getDays(year, month);

  // 如果当前 day 超出该月天数则修正
  useEffect(() => {
    if (!days.includes(day)) setDay(days[days.length - 1]);
  }, [days, day]);

  // 任何列变化都向上通知
  useEffect(() => {
    onChange(`${year}-${month}-${day}T${hour}:${min}`);
  }, [year, month, day, hour, min, onChange]);

  return (
    <div style={dp.wrap}>
      <div style={dp.labels}>
        {['年','月','日','时','分'].map(l => (
          <div key={l} style={dp.lbl}>{l}</div>
        ))}
      </div>
      <div style={dp.picker}>
        <ScrollColumn items={YEARS}  value={year}  onChange={setYear}  />
        <ScrollColumn items={MONTHS} value={month} onChange={setMonth} />
        <ScrollColumn items={days}   value={day}   onChange={setDay}   />
        <ScrollColumn items={HOURS}  value={hour}  onChange={setHour}  />
        <ScrollColumn items={MINS}   value={min}   onChange={setMin}   />
      </div>
    </div>
  );
};

const dp: Record<string, React.CSSProperties> = {
  wrap: {
    background: '#0f1629',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    overflow: 'hidden',
    userSelect: 'none' as const,
  },
  labels: {
    display: 'flex',
    borderBottom: '1px solid #1e2d4a',
  },
  lbl: {
    flex: 1,
    textAlign: 'center' as const,
    fontSize: 11,
    color: '#6b7d99',
    padding: '6px 0',
    fontWeight: 600,
    letterSpacing: '0.5px',
  },
  picker: {
    display: 'flex',
    gap: 0,
  },
};

// ---- Main Component ----
const AnnouncementPage: React.FC<Props> = ({ account, signer }) => {
  const [list, setList] = useState<Announcement[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // form fields
  const [contractAddr, setContractAddr] = useState('');
  const [tokenInfo, setTokenInfo] = useState<{ name: string; symbol: string; totalSupply: string } | null>(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [estCirculation, setEstCirculation] = useState('');
  const [launchDateTime, setLaunchDateTime] = useState('');
  const [desc, setDesc] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoadingList(true);
    const rows = await fetchAnnouncements();
    setList(rows);
    setLoadingList(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // 读取链上代币信息
  const handleFetchToken = async () => {
    if (!signer || !contractAddr.trim()) return;
    if (!ethers.utils.isAddress(contractAddr.trim())) {
      toast.error('合约地址格式不正确');
      return;
    }
    setLoadingToken(true);
    try {
      const erc20 = new ethers.Contract(contractAddr.trim(), ERC20_ABI, signer);
      const [name, symbol, decimals, totalSupply] = await Promise.all([
        erc20.name(),
        erc20.symbol(),
        erc20.decimals(),
        erc20.totalSupply(),
      ]);
      setTokenInfo({
        name,
        symbol,
        totalSupply: parseFloat(ethers.utils.formatUnits(totalSupply, decimals)).toLocaleString(),
      });
    } catch {
      toast.error('读取代币信息失败，请检查合约地址');
    } finally {
      setLoadingToken(false);
    }
  };

  // 发布预告：先扣1000 DUSD到黑洞，再写 Supabase
  const handleSubmit = async () => {
    if (!signer || !account) { toast.error('请先连接钱包'); return; }
    if (!tokenInfo) { toast.error('请先读取代币信息'); return; }
    if (!estCirculation.trim()) { toast.error('请填写预估流通量'); return; }
    if (!launchDateTime) { toast.error('请选择计划上线时间'); return; }

    setSubmitting(true);
    try {
      // Step 1: 检查 DUSD 余额
      const dusd = new ethers.Contract(DUSD_ADDRESS, ERC20_ABI, signer);
      const feeBN = ethers.utils.parseUnits(ANNOUNCE_FEE, 18);
      const balance: ethers.BigNumber = await dusd.balanceOf(account);
      if (balance.lt(feeBN)) {
        const has = parseFloat(ethers.utils.formatUnits(balance, 18)).toFixed(2);
        throw new Error(`DUSD 余额不足，需要 1,000 DUSD，当前 ${has} DUSD`);
      }

      // Step 2: 转账 1000 DUSD 到黑洞
      toast.loading('正在发送 1000 DUSD 到黑洞地址…', { id: 'ann-tx' });
      const tx = await dusd.transfer(BLACKHOLE, feeBN);
      await tx.wait();
      toast.success('费用已销毁', { id: 'ann-tx' });

      // Step 3: 写入 Supabase
      const ok = await insertAnnouncement({
        token_address: contractAddr.trim().toLowerCase(),
        token_name: tokenInfo.name,
        token_symbol: tokenInfo.symbol,
        total_supply: tokenInfo.totalSupply,
        estimated_circulation: estCirculation.trim(),
        planned_launch_time: launchDateTime
          ? new Date(launchDateTime).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
          : '',
        description: desc.trim(),
        website: website.trim(),
        creator: account,
        tx_hash: tx.hash,
      });
      if (!ok) throw new Error('预告保存失败，请联系管理员');

      toast.success('预告发布成功！');
      setShowForm(false);
      setContractAddr(''); setTokenInfo(null);
      setEstCirculation(''); setLaunchDateTime('');
      setDesc(''); setWebsite('');
      load();
    } catch (e: any) {
      toast.error(e.message || '发布失败', { id: 'ann-tx' });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setContractAddr(''); setTokenInfo(null);
    setEstCirculation(''); setLaunchDateTime('');
    setDesc(''); setWebsite('');
  };

  return (
    <div style={s.page}>
      {/* Header row */}
      <div style={s.topRow}>
        <div>
          <h1 style={s.title}>代币上线预告</h1>
          <p style={s.subtitle}>提前公示即将上线的代币信息，增加社区透明度</p>
        </div>
        {account && !showForm && (
          <button style={s.publishBtn} onClick={() => setShowForm(true)}>
            + 发布预告
          </button>
        )}
        {!account && (
          <div style={s.noWallet}>请先连接钱包</div>
        )}
      </div>

      {/* Publish form */}
      {showForm && (
        <div style={s.formCard}>
          <div style={s.formTitle}>发布新预告</div>
          <div style={s.feeTip}>
            💸 发布预告需销毁 <strong>1,000 DUSD</strong>（直接转入黑洞地址，不可退款）
          </div>

          {/* Contract address */}
          <div style={s.field}>
            <label style={s.label}>代币合约地址 *</label>
            <div style={s.addrRow}>
              <input
                style={s.input}
                placeholder="0x..."
                value={contractAddr}
                onChange={e => { setContractAddr(e.target.value); setTokenInfo(null); }}
              />
              <button
                style={s.fetchBtn}
                onClick={handleFetchToken}
                disabled={loadingToken || !contractAddr.trim()}
              >
                {loadingToken ? '读取中…' : '读取'}
              </button>
            </div>
          </div>

          {/* Token info (readonly) */}
          {tokenInfo && (
            <div style={s.tokenInfoBox}>
              <div style={s.infoRow}>
                <span style={s.infoLabel}>代币名称</span>
                <span style={s.infoVal}>{tokenInfo.name}</span>
              </div>
              <div style={s.infoRow}>
                <span style={s.infoLabel}>代币符号</span>
                <span style={s.infoVal}>${tokenInfo.symbol}</span>
              </div>
              <div style={s.infoRow}>
                <span style={s.infoLabel}>总供应量</span>
                <span style={s.infoVal}>{tokenInfo.totalSupply}</span>
              </div>
            </div>
          )}

          {/* Estimated circulation */}
          <div style={s.field}>
            <label style={s.label}>预估流通量 *</label>
            <div style={s.addrRow}>
              <input
                style={s.input}
                placeholder="例：1,000,000"
                value={estCirculation}
                onChange={e => setEstCirculation(e.target.value)}
              />
              {tokenInfo && (
                <button
                  style={s.fetchBtn}
                  type="button"
                  onClick={() => setEstCirculation(tokenInfo.totalSupply)}
                >
                  全部
                </button>
              )}
            </div>
          </div>

          {/* Planned launch time */}
          <div style={s.field}>
            <label style={s.label}>
              计划上线时间 *
              {launchDateTime && (
                <span style={s.dtPreview}>
                  {new Date(launchDateTime).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </label>
            <DateTimePicker onChange={setLaunchDateTime} />
          </div>

          {/* Description */}
          <div style={s.field}>
            <label style={s.label}>代币简介 <span style={s.optional}>（可选）</span></label>
            <textarea
              style={{ ...s.input, ...s.textarea }}
              placeholder="简要介绍代币的用途、背景等..."
              value={desc}
              onChange={e => setDesc(e.target.value)}
              maxLength={500}
            />
            <div style={s.charCount}>{desc.length} / 500</div>
          </div>

          {/* Website */}
          <div style={s.field}>
            <label style={s.label}>官网链接 <span style={s.optional}>（可选）</span></label>
            <input
              style={s.input}
              placeholder="https://..."
              value={website}
              onChange={e => setWebsite(e.target.value)}
            />
          </div>

          <div style={s.formActions}>
            <button style={s.cancelBtn} onClick={resetForm} disabled={submitting}>取消</button>
            <button
              style={{ ...s.submitBtn, ...(submitting ? s.submitBtnDisabled : {}) }}
              onClick={handleSubmit}
              disabled={submitting || !tokenInfo}
            >
              {submitting ? '处理中…' : '确认发布（销毁 1,000 DUSD）'}
            </button>
          </div>
        </div>
      )}

      {/* Announcement list */}
      {loadingList ? (
        <div style={s.loading}>加载中…</div>
      ) : list.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>📢</div>
          <div style={s.emptyText}>暂无预告，成为第一个发布预告的项目方</div>
        </div>
      ) : (
        <div style={s.list}>
          {list.map(ann => (
            <div key={ann.id} style={s.card}>
              <div style={s.cardHeader}>
                <div style={s.tokenBadge}>{ann.token_symbol}</div>
                <div style={s.cardName}>{ann.token_name}</div>
                <div style={s.cardDate}>{new Date(ann.created_at).toLocaleDateString('zh-CN')}</div>
              </div>

              <div style={s.cardGrid}>
                <div style={s.gridItem}>
                  <span style={s.gridLabel}>合约地址</span>
                  <span style={{ ...s.gridVal, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <a
                      href={`https://bscscan.com/token/${ann.token_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.link}
                    >
                      {ann.token_address.slice(0, 8)}…{ann.token_address.slice(-6)}
                    </a>
                    <button
                      style={s.addrCopyBtn}
                      title="复制合约地址"
                      onClick={() => {
                        navigator.clipboard.writeText(ann.token_address).then(() => {
                          toast.success('合约地址已复制');
                        }).catch(() => {
                          toast.error('复制失败');
                        });
                      }}
                    >
                      ⧉
                    </button>
                  </span>
                </div>
                <div style={s.gridItem}>
                  <span style={s.gridLabel}>总供应量</span>
                  <span style={s.gridVal}>{ann.total_supply}</span>
                </div>
                <div style={s.gridItem}>
                  <span style={s.gridLabel}>预估流通量</span>
                  <span style={s.gridVal}>{ann.estimated_circulation}</span>
                </div>
                <div style={s.gridItem}>
                  <span style={s.gridLabel}>计划上线时间</span>
                  <span style={s.gridVal}>{ann.planned_launch_time}</span>
                </div>
                <div style={s.gridItem}>
                  <span style={s.gridLabel}>发布者</span>
                  <span style={s.gridVal}>{ann.creator.slice(0, 6)}…{ann.creator.slice(-4)}</span>
                </div>
                {ann.website && (
                  <div style={s.gridItem}>
                    <span style={s.gridLabel}>官网</span>
                    <a
                      href={ann.website.startsWith('http') ? ann.website : `https://${ann.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.websiteLink}
                      title={ann.website}
                    >
                      {ann.website}
                    </a>
                  </div>
                )}
              </div>

              {ann.description && (
                <div style={s.cardDesc}>{ann.description}</div>
              )}

              <div style={s.cardFooter}>
                <a
                  href={`https://bscscan.com/tx/${ann.tx_hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={s.txLink}
                >
                  🔗 费用销毁凭证
                </a>
                <button
                  style={s.copyBtn}
                  title="复制预告分享链接"
                  onClick={() => {
                    const url = `${window.location.origin}${window.location.pathname}?ann=${ann.id}`;
                    navigator.clipboard.writeText(url).then(() => {
                      toast.success('分享链接已复制');
                    }).catch(() => {
                      toast.error('复制失败');
                    });
                  }}
                >
                  🔗 分享
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Styles ----
const s: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 860,
    margin: '0 auto',
    padding: '32px 16px 120px',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
    flexWrap: 'wrap',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: 800,
    color: '#e8eef7',
    margin: 0,
    marginBottom: 6,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7d99',
    margin: 0,
  },
  publishBtn: {
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    padding: '10px 22px',
    borderRadius: 10,
    cursor: 'pointer',
    boxShadow: '0 0 16px rgba(0,212,255,0.3)',
    flexShrink: 0,
  },
  noWallet: {
    fontSize: 13,
    color: '#6b7d99',
    padding: '10px 0',
  },
  // Form
  formCard: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    padding: '24px',
    marginBottom: 32,
  },
  formTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: '#e8eef7',
    marginBottom: 12,
  },
  feeTip: {
    fontSize: 13,
    color: '#ffd600',
    background: 'rgba(255,214,0,0.07)',
    border: '1px solid rgba(255,214,0,0.2)',
    borderRadius: 8,
    padding: '10px 14px',
    marginBottom: 20,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 12,
    color: '#6b7d99',
    fontWeight: 600,
    marginBottom: 6,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  optional: {
    color: '#3d5070',
    fontWeight: 400,
    textTransform: 'none' as const,
    letterSpacing: 0,
  },
  addrRow: {
    display: 'flex',
    gap: 8,
  },
  input: {
    width: '100%',
    background: '#0f1629',
    border: '1px solid #1e2d4a',
    borderRadius: 10,
    color: '#e8eef7',
    fontSize: 14,
    padding: '11px 14px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
    WebkitAppearance: 'none' as const,
    appearance: 'none' as const,
  },
  textarea: {
    minHeight: 90,
    resize: 'vertical' as const,
  },
  charCount: {
    textAlign: 'right' as const,
    fontSize: 11,
    color: '#3d5070',
    marginTop: 4,
  },
  dtPreview: {
    marginLeft: 8,
    fontSize: 12,
    color: '#00d4ff',
    fontWeight: 500,
    textTransform: 'none' as const,
    letterSpacing: 0,
  },
  fetchBtn: {
    background: 'rgba(0,212,255,0.1)',
    border: '1px solid rgba(0,212,255,0.3)',
    color: '#00d4ff',
    fontSize: 13,
    fontWeight: 600,
    padding: '0 18px',
    borderRadius: 10,
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap' as const,
  },
  tokenInfoBox: {
    background: 'rgba(0,212,255,0.04)',
    border: '1px solid rgba(0,212,255,0.15)',
    borderRadius: 10,
    padding: '12px 16px',
    marginBottom: 16,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: { fontSize: 12, color: '#6b7d99' },
  infoVal: { fontSize: 14, fontWeight: 600, color: '#e8eef7' },
  formActions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  cancelBtn: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid #1e2d4a',
    color: '#6b7d99',
    fontSize: 14,
    fontWeight: 600,
    padding: '10px 22px',
    borderRadius: 10,
    cursor: 'pointer',
  },
  submitBtn: {
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    padding: '10px 24px',
    borderRadius: 10,
    cursor: 'pointer',
  },
  submitBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed' as const,
  },
  // List
  loading: {
    textAlign: 'center' as const,
    color: '#6b7d99',
    padding: '60px 0',
    fontSize: 14,
  },
  empty: {
    textAlign: 'center' as const,
    padding: '80px 0',
  },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyText: { color: '#6b7d99', fontSize: 14 },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  card: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    padding: '20px 24px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  tokenBadge: {
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    padding: '4px 10px',
    borderRadius: 6,
    flexShrink: 0,
  },
  cardName: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e8eef7',
    flex: 1,
  },
  cardDate: {
    fontSize: 12,
    color: '#3d5070',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '10px 20px',
    marginBottom: 14,
  },
  gridItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 3,
  },
  gridLabel: {
    fontSize: 11,
    color: '#3d5070',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  gridVal: {
    fontSize: 13,
    color: '#a0b0c8',
    fontWeight: 500,
  },
  cardDesc: {
    fontSize: 13,
    color: '#6b7d99',
    lineHeight: 1.6,
    borderTop: '1px solid rgba(255,255,255,0.05)',
    paddingTop: 12,
    marginTop: 4,
    whiteSpace: 'pre-wrap' as const,
  },
  cardFooter: {
    marginTop: 12,
    paddingTop: 10,
    borderTop: '1px solid rgba(255,255,255,0.04)',
    display: 'flex',
    gap: 12,
  },
  link: {
    color: '#00d4ff',
    textDecoration: 'none',
    fontSize: 13,
  },
  websiteLink: {
    color: '#00d4ff',
    textDecoration: 'none',
    fontSize: 13,
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: '100%',
  },
  txLink: {
    color: '#3d5070',
    textDecoration: 'none',
    fontSize: 12,
    flex: 1,
  },
  copyBtn: {
    background: 'none',
    border: 'none',
    color: '#3d5070',
    fontSize: 12,
    cursor: 'pointer',
    padding: '0 4px',
    flexShrink: 0,
  },
  addrCopyBtn: {
    background: 'none',
    border: 'none',
    color: '#3d5070',
    fontSize: 14,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    flexShrink: 0,
    transition: 'color 0.15s',
  },
};

export default AnnouncementPage;
